import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import { Typography, FormControl, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import {
    DataGrid,
    GridFilterModel, GridSortModel, GridLinkOperator, GridRowId, GridColDef
} from "@mui/x-data-grid";

import { useAuth } from "../../_auth/useAuth";
import {
    DaGridContainer, DaGridLoadingOverlay, DaGridSettings, ListViewToolbar,
    DialogBox, IDialogTextProps,
    MessageBox, MsgBoxType, IMessagePanelProps,
    MuiGridStateProp, MuiGridDataProp, muiGridFilterIsValid, FormTitle,
    Toast
} from "../_shared";
import { IItemIdsRequest, IMuiDataRequest, pafApi } from "../../_api";
import { AuthRolesEnum, pafViewColumns } from "../../_appData";
import { isRowSelectable } from "../../_appData/grids/pafGrids";
import { SignatureStatus, SignatureStatusDisplay } from "../../_appData/enums/signatureStatus";
import { createActionsColumn } from "../../_appData/grids/actionsColumn";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButton from '@mui/material/IconButton';

export const PafListView: React.FC = () => {
    const { isAuthenticated, userHasPrivilege, getAccessToken } = useAuth();
    const navigate = useNavigate();
    const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
    const getGridDataRef = useRef<() => Promise<void>>();
    
    // Check if user is Broker, ListAdmin, or Licensee to hide edit icon and disable checkboxes
    const isBroker = userHasPrivilege([AuthRolesEnum.BrokerAgent]);
    const isListAdmin = userHasPrivilege([AuthRolesEnum.ListAdmin]);
    const isLicensee = userHasPrivilege([AuthRolesEnum.Licensee]);
    const isBrokerOrListAdminOrLicensee = isBroker || isListAdmin || isLicensee;
    
    const msgBox_Show = (message: string, title?: string, type?: MsgBoxType) => {
        setMsgBoxProps({
            message: message,
            title: title,
            type: type,
        })
        setMsgBoxOpen(true);
    }

    const handlePdfDownload = useCallback(async (row: any) => {
        try {
            setIsLoading(true);
            const auth_token = await getAccessToken();
            const paf_id = row.paf_id;
            const filename = `PAF_${row.paf_sid || paf_id}.pdf`;

            // Use the updated pdf_download function
            const resp = await pafApi.pdf_download(paf_id, auth_token, filename);

            if (resp && resp.success) {
                // Show toast message for success instead of dialog
                setSnackbar({
                    open: true,
                    message: resp.message || "PDF downloaded successfully.",
                    severity: 'success'
                });
            } else {
                throw new Error(resp?.message || 'Failed to download PDF');
            }
        } catch (error: any) {
            // Show toast message for error as well
            setSnackbar({
                open: true,
                message: error?.message || "An error occurred while downloading the PDF. Please try again.",
                severity: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    }, [getAccessToken]);
    
    // Create dynamic columns with conditional edit icon visibility
    const columns = useMemo(() => {
        const baseColumns = [...pafViewColumns];
        // Replace the actions column with one that hides edit for Broker, ListAdmin, and Licensee
        const actionsColumnIndex = baseColumns.findIndex(col => col.field === 'actions');
        if (actionsColumnIndex !== -1) {
            baseColumns[actionsColumnIndex] = createActionsColumn({
                headerName: 'Actions',
                width: 80,
                showView: true,
                showEdit: !isBrokerOrListAdminOrLicensee, // Hide edit icon for Broker, ListAdmin, and Licensee
                getViewUrl: (row: any) => `/paf/${row.paf_id}?mode=view`,
                getEditUrl: (row: any) => `/paf/${row.paf_id}`,
                shouldShowEdit: (row: any) => {
                    // If Broker, ListAdmin, or Licensee, never show edit
                    if (isBrokerOrListAdminOrLicensee) return false;
                    // Never show edit for EXPIRED status
                    if (row.signature_status === 'EXPIRED') return false;
                    // Never show edit for REJECTED status
                    if (row.signature_status === 'REJECTED') return false;
                    // Allow edit (and thus e-sign) if pending_signatures is exactly ['list_owner']
                    if (Array.isArray(row.pending_signatures) && row.pending_signatures.length === 1 && row.pending_signatures[0] === 'list_owner') {
                        return true;
                    }
                    return row.signature_status !== 'APPROVED' && row.signature_status !== 'WAITING_APPROVAL' && row.signature_status !== 'WAITING_SIGNATURES';
                },
                iconGapPx: 16,
            });

            // Add PDF download column right after actions column (before paf_sid, not after company)
            const pdfDownloadColumn: GridColDef = {
                field: 'pdf_download',
                headerName: '',
                width: 60,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: any) => {
                    return (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handlePdfDownload(params.row);
                            }}
                            sx={{ 
                                padding: '4px',
                                color: 'primary.main',
                                '&:hover': {
                                    backgroundColor: 'action.hover',
                                }
                            }}
                        >
                            <FileDownloadIcon fontSize="small" />
                        </IconButton>
                    );
                },
            };
            // Insert right after actions column (at index actionsColumnIndex + 1)
            // This ensures it comes before paf_sid and company__company_name
            baseColumns.splice(actionsColumnIndex + 1, 0, pdfDownloadColumn);
        }
        return baseColumns;
    }, [isBrokerOrListAdminOrLicensee, handlePdfDownload]);
    const [dlgBoxOpen, setDlgBoxOpen] = React.useState<boolean>(false);
    const [dlgBoxProps, setDlgBoxProps] = useState<IDialogTextProps>({ message: "" });
    const [msgBoxOpen, setMsgBoxOpen] = React.useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<IMessagePanelProps>({ message: "" });
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [gridState, setGridState] = useState<MuiGridStateProp>({
        page: 0,
        page_size: DaGridSettings.defaultPageSize,
        filters: {
            items: [],
            linkOperator: GridLinkOperator.Or
        },
        sorts: [
            { field: 'modify_datetime', sort: 'desc' }
        ],
    });
    const [gridData, setGridData] = useState<MuiGridDataProp>({
        rows: [],
        row_count: 0,
    });
    const [selectedStatus, setSelectedStatus] = useState<string>('');

    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        setSelectedStatus(event.target.value as SignatureStatus);
    };

    const deleteSelectedItems = useCallback(async (row_ids: Array<string | number>) => {
        setIsLoading(true);

        const options: IItemIdsRequest = { item_ids: row_ids };
        const auth_token: string = await getAccessToken();

        pafApi.remove(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        // Call getGridData via ref to refresh the grid
                        if (getGridDataRef.current) {
                            getGridDataRef.current();
                        }
                        msgBox_Show(
                            `${resp.count} ListAdmin items have been deleted...`,
                            undefined,
                            "info"
                        );
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while deleting the Paf... " +
                            "please retry you last action. If you continue to experience issues, " +
                            "contact customer support.",
                            "Unable to Delete the selected PAFs...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [msgBox_Show, getAccessToken]);

    const dlgBox_Close = useCallback((value?: string) => {
        const selRows = selectionModel;
        setDlgBoxOpen(false);

        if (value && value === "ok") {
            // delete
            deleteSelectedItems(selRows as (string | number)[]);
        }
    }, [selectionModel, deleteSelectedItems]);

    const dlgBox_Show = (message: string, title?: string) => {
        setDlgBoxProps({
            message: message,
            title: title,
        })
        setDlgBoxOpen(true);
    }

    const msgBox_Close = (value?: string) => {
        setMsgBoxOpen(false);
    }
    
    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    }

    const onGridPageChange = (page: number) => {
        setGridState((prev) => ({
            ...prev,
            page: page
        }));
    };

    const onGridPageSizeChange = (size: number) => {
        setGridState((prev) => ({
            ...prev,
            page_size: size
        }));
    };

    const onGridFilterChange = (model: GridFilterModel) => {
        if (muiGridFilterIsValid(model)) {
            setGridState((prev) => ({
                ...prev,
                filters: model
            }));
        }
    };

    const onGridSortChange = (model: GridSortModel) => {
        setGridState((prev) => ({
            ...prev,
            sorts: model
        }));
    };

    const onClickNew = (e: React.MouseEvent<HTMLButtonElement>) => {
        navigate("/paf/new");
    };

    const onClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selRows = selectionModel;
        if (selRows.length === 0)
            msgBox_Show(
                "No item selected... select one item only to be edited",
                "Edit PAF...",
                "warning"
            );
        if (selRows.length > 1)
            msgBox_Show(
                "Multiple items selected... select one item only to be edited",
                "Edit PAF...",
                "warning"
            );

        if (selRows.length === 1) {
            navigate(`/paf/${selRows[0]}`);
        }
    };

    const onClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        // see dlgBox_close for processing of dialog result
        dlgBox_Show(
            "Please confirm that you wish to delete the selected item(s).",
            "Delete Selected Item(s)?"
        );
    };

    const onClickView = (e: React.MouseEvent<HTMLButtonElement>) => {
        const selRows = selectionModel;
        if (selRows.length === 0)
            msgBox_Show(
                "No item selected... select one item only to be viewed",
                "View PAF...",
                "warning"
            );
        if (selRows.length > 1)
            msgBox_Show(
                "Multiple items selected... select one item only to be viewed",
                "View PAF...",
                "warning"
            );

        if (selRows.length === 1) {
            navigate(`/paf/${selRows[0]}?mode=view`);
        }
    };

    const execSearch = (term: string) => {
        // Modify Grid Filter model
        let model: GridFilterModel = {
            items: [],
            linkOperator: GridLinkOperator.Or
        }
        // Search Broker_SId, CompanyName
        const t = typeof term === 'string' ? term : '';
        if (t.length > 0) {
            model.items.push({ id: 1001, columnField: "paf_sid", operatorValue: "contains", value: t });
            model.items.push({ id: 1002, columnField: "company_name", operatorValue: "contains", value: t });
        }

        setGridState((prev) => ({
            ...prev,
            filters: model
        }));
    };

    const onClickReset = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSelectedStatus('');
        setGridState((prev) => ({
            ...prev,
            filters: {
                items: [],
                linkOperator: GridLinkOperator.Or
            },
            sorts: []
        }));
    };

    const getGridData = useCallback(async () => {
        setIsLoading(true);

        // Remove any signature_status from filters if it somehow got added
        const cleanedFilters = {
            items: (gridState.filters?.items || []).filter(
                item => item.columnField !== 'signature_status'
            ),
            linkOperator: gridState.filters?.linkOperator || GridLinkOperator.Or
        };

        // Build the API request with signature_status as a separate field
        const options: IMuiDataRequest = {
            page: gridState.page,
            page_size: gridState.page_size,
            filters: cleanedFilters,
            sorts: gridState.sorts,
            // Only include signature_status if a valid status is selected (not empty/all)
            ...(selectedStatus && { signature_status: selectedStatus })
        };

        const auth_token: string = await getAccessToken();

        pafApi.get(options, auth_token)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        setGridData({
                            rows: resp.data,
                            row_count: resp.count!
                        });
                    } else if (resp.error && resp.error.length > 0) {
                        msgBox_Show(
                            "An error occurred while retrieving PAF data... \n" +
                            "please retry your last action. If you continue to experience issues, " +
                            "please contact customer support.",
                            "Unable to Load PAF data...",
                            "warning"
                        );
                    }
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [gridState, selectedStatus, getAccessToken, msgBox_Show]);

    // Store getGridData in ref so it can be called from deleteSelectedItems
    useEffect(() => {
        getGridDataRef.current = getGridData;
    }, [getGridData]);

    // Track previous values to prevent unnecessary API calls
    const prevValuesRef = useRef<string>('');

    useEffect(() => {
        // Create a stable key from the values that matter
        const currentKey = JSON.stringify({
            page: gridState.page,
            page_size: gridState.page_size,
            filters: gridState.filters,
            sorts: gridState.sorts,
            selectedStatus: selectedStatus
        });

        // Only call API if values actually changed
        if (prevValuesRef.current !== currentKey) {
            prevValuesRef.current = currentKey;
            getGridData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridState.page, gridState.page_size, gridState.filters, gridState.sorts, selectedStatus]);

    const lvGridToolbar = () => {
        const isAdmin = userHasPrivilege([AuthRolesEnum.BrokerAgent, AuthRolesEnum.ListAdmin,
        AuthRolesEnum.ListOwner, AuthRolesEnum.Licensee, AuthRolesEnum.SystemAdmin]);
        const isROUser = userHasPrivilege([AuthRolesEnum.PafUser]);
        // Licensee can add new PAFs, but Broker and ListAdmin cannot
        const canAddNew = isAdmin && !isBroker && !isListAdmin;
        return (
            <ListViewToolbar
                onClickNew={onClickNew}
                onClickEdit={onClickEdit}
                onClickDelete={onClickDelete}
                onClickReset={onClickReset}
                execSearch={execSearch}
                canView={isAdmin || isROUser}
                canAdd={canAddNew}
                canUpdate={isAdmin && !isBrokerOrListAdminOrLicensee}
                canDelete={isAdmin && !isBrokerOrListAdminOrLicensee}
            />
        );
    }

    return (
        <>
            <Box
                sx={{
                    paddingTop: 3,
                    marginBottom: { xs: 4, sm: 6 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: { xs: 'flex-start', sm: 'space-between' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        mb: 2,
                        gap: { xs: 2, sm: 0 },
                    }}
                >
                    <Box sx={{ mb: { xs: 1, sm: 0 }, textAlign: { xs: 'center', sm: 'left' } }}>
                        <FormTitle
                            title={"PAFs"}
                            typeVariant={"h4"}
                            titleIcon={"assignment_rounded_icon"}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            Status:
                        </Typography>
                        <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: 120, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
                            <Select
                                id="status-filter"
                                value={selectedStatus}
                                onChange={handleStatusChange}
                                displayEmpty
                                sx={{
                                    backgroundColor: 'background.paper',
                                    width: { xs: '100%', sm: 'auto' },
                                    '& .MuiSelect-select': {
                                        paddingY: 1,
                                    }
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                {Object.entries(SignatureStatusDisplay).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>
                                        {label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                {isAuthenticated ? (
                    <Box sx={{ marginTop: 2 }}>
                        <DaGridContainer
                            sx={{
                                height: 600,
                                width: '100%',
                            }}
                        >
                            <DataGrid
                                components={{
                                    LoadingOverlay: DaGridLoadingOverlay,
                                    Toolbar: lvGridToolbar,
                                }}
                                loading={isLoading}
                                columns={columns}
                                rows={gridData.rows}
                                rowCount={gridData.row_count}
                                getRowId={(row) => row.paf_id}
                                isRowSelectable={(params) => {
                                    // Disable checkboxes for Broker, ListAdmin, and Licensee users
                                    if (isBrokerOrListAdminOrLicensee) return false;
                                    return isRowSelectable(params.row);
                                }}
                                filterMode="server"
                                paginationMode="server"
                                sortingMode="server"
                                sortModel={gridState.sorts}
                                pagination
                                page={gridState.page}
                                pageSize={gridState.page_size}
                                onFilterModelChange={onGridFilterChange}
                                onSortModelChange={onGridSortChange}
                                onPageChange={onGridPageChange}
                                onPageSizeChange={onGridPageSizeChange}
                                rowsPerPageOptions={DaGridSettings.rowsPerPageOptions}
                                headerHeight={DaGridSettings.headerRowHeight}
                                rowHeight={DaGridSettings.rowHeight}
                                checkboxSelection
                                disableSelectionOnClick
                                selectionModel={selectionModel}
                                onSelectionModelChange={(newSelection) => {
                                    setSelectionModel(newSelection);
                                }}
                                columnVisibilityModel={{
                                    paf_id: false,
                                    // federal_tax_id: false,
                                }}
                            />
                        </DaGridContainer>
                    </Box>
                ) : (
                    <Typography variant="h5">Please Login to use this system...</Typography>
                )}
            </Box>
            <Toast
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
            />
            <MessageBox
                isOpen={msgBoxOpen}
                onClose={msgBox_Close}
                {...msgBoxProps}
            />
            <DialogBox
                isOpen={dlgBoxOpen}
                onClose={dlgBox_Close}
                {...dlgBoxProps}
            />
        </>
    );
}
